import type { ReactElement, ReactNode } from 'react';
import { useDisplayTimeZone } from '@/hooks/useDisplayTimeZone';

import { BitbucketIcon } from '@/components/bitbucket/BitbucketIcon';
import { Button } from '@/components/ui/button';
import type {
  BitbucketStatusResponse,
  BitbucketSyncStatusResponse,
} from '@kirin/api-client/contracts/bitbucket';

import { useTimestampFormat } from '@/hooks/useTimestampFormat';

import { IntegrationCard } from './IntegrationCard';
import { BitbucketTokenHealthBanner } from './BitbucketTokenHealthBanner';
import {
  renderConnectButton,
  renderDisconnectButton,
  renderErrorAlert,
  renderErrorBadgeSlot,
  renderMeta,
  renderRunningIndicator,
  renderStatusCopy,
  renderSyncButton,
  renderSyncNote,
  renderTryAgainButton,
  type SyncCardStatus,
  type SyncRendererConfig,
} from './syncIntegrationRenderers';

export type BitbucketCardStatus = SyncCardStatus;

export interface BitbucketIntegrationCardProps {
  status: BitbucketStatusResponse;
  syncStatus?: BitbucketSyncStatusResponse;
  onConnect?: () => void;
  onManage?: () => void;
  onDisconnect?: () => void;
  onUpdateToken?: () => void;
  cardStatus?: BitbucketCardStatus;
  onSync?: () => void;
  isLoading?: boolean;
  isSyncing?: boolean;
  error?: string;
  connectTooltip?: string;
  syncNote?: string;
  readOnly?: boolean;
}

const CFG: SyncRendererConfig = {
  testIdPrefix: 'bitbucket',
  displayName: 'Bitbucket',
};

function renderBody(
  cardStatus: BitbucketCardStatus,
  connected: boolean,
  status: BitbucketStatusResponse,
  syncStatus: BitbucketSyncStatusResponse | undefined,
  syncNote: string | undefined,
  error: string | undefined,
  onUpdateToken: (() => void) | undefined
): ReactNode {
  const showBanner =
    connected && Boolean(status.healthState) && status.healthState !== 'active';
  const showRunning = connected && syncStatus?.isRunning === true;
  const showNote = cardStatus === 'connected';
  const showError = cardStatus === 'error' && Boolean(error);
  const statusCopy = renderStatusCopy(CFG, cardStatus);

  if (
    !showBanner &&
    !showRunning &&
    !showNote &&
    !showError &&
    statusCopy === null
  ) {
    return null;
  }

  return (
    <IntegrationCard.Body>
      {statusCopy}
      {showBanner && status.healthState && (
        <BitbucketTokenHealthBanner
          healthState={status.healthState}
          tokenExpiresAt={status.tokenExpiresAt ?? undefined}
          onUpdateToken={onUpdateToken}
        />
      )}
      {showRunning && renderRunningIndicator(CFG, syncStatus?.isRunning)}
      {showNote && renderSyncNote(CFG, syncNote)}
      {showError && renderErrorAlert(CFG, error)}
    </IntegrationCard.Body>
  );
}

function renderManageButton(
  onManage: (() => void) | undefined,
  readOnly?: boolean
): ReactNode {
  if (!onManage) return null;
  return (
    <Button variant="outline" size="sm" onClick={onManage} disabled={readOnly}>
      Manage
    </Button>
  );
}

function renderActions(
  cardStatus: BitbucketCardStatus,
  props: BitbucketIntegrationCardProps
): ReactNode {
  const {
    onConnect,
    onManage,
    onDisconnect,
    onSync,
    isSyncing = false,
    connectTooltip,
    readOnly,
  } = props;
  const tooltip = connectTooltip ?? 'Connect your Bitbucket workspace';

  if (cardStatus === 'connected') {
    if (!onSync && !onManage && !onDisconnect) return null;
    return (
      <IntegrationCard.Actions>
        {renderSyncButton(CFG, { onSync, isSyncing, readOnly })}
        {renderManageButton(onManage, readOnly)}
        {renderDisconnectButton(CFG, { onDisconnect, isSyncing, readOnly })}
      </IntegrationCard.Actions>
    );
  }

  if (cardStatus === 'error') {
    if (!onConnect && !onDisconnect) return null;
    return (
      <IntegrationCard.Actions>
        {renderTryAgainButton(CFG, onConnect, readOnly)}
        {renderDisconnectButton(CFG, { onDisconnect, isSyncing, readOnly })}
      </IntegrationCard.Actions>
    );
  }

  if (!onConnect) return null;
  return (
    <IntegrationCard.Actions>
      {renderConnectButton(CFG, { onConnect, tooltip, readOnly })}
    </IntegrationCard.Actions>
  );
}

export function BitbucketIntegrationCard(
  props: BitbucketIntegrationCardProps
): ReactElement {
  const { status, syncStatus, onUpdateToken } = props;
  const { value: timestampFormat } = useTimestampFormat();
  const timeZone = useDisplayTimeZone();
  const connected = status.connected === true;
  // Fallback only derives connected | not_connected. Callers that want the
  // 'error' card variant must pass cardStatus explicitly.
  const cardStatus: BitbucketCardStatus =
    props.cardStatus ?? (connected ? 'connected' : 'not_connected');
  const primitiveStatus: 'connected' | 'not_configured' =
    cardStatus === 'connected' ? 'connected' : 'not_configured';

  return (
    <IntegrationCard
      icon={<BitbucketIcon className="h-5 w-5" />}
      title="Bitbucket"
      subtitle="Connect your Bitbucket workspace"
      status={primitiveStatus}
      isLoading={props.isLoading}
    >
      {renderErrorBadgeSlot(CFG, cardStatus)}
      {renderMeta(CFG, {
        orgLabel: 'Org',
        orgName: status.workspaceName,
        syncLabel: 'Sync',
        importedCount: syncStatus?.importedCount ?? status.importedCount,
        linkedCount: syncStatus?.linkedCount,
        lastSyncAt: syncStatus?.lastSyncAt,
        lastSyncStatus: syncStatus?.lastSyncStatus,
        connectedAt: status.connectedAt,
        hasSyncStatus: syncStatus !== undefined,
        timestampFormat,
        timeZone,
      })}
      {renderBody(
        cardStatus,
        connected,
        status,
        syncStatus,
        props.syncNote,
        props.error,
        onUpdateToken
      )}
      {renderActions(cardStatus, props)}
    </IntegrationCard>
  );
}

BitbucketIntegrationCard.displayName = 'BitbucketIntegrationCard';
